/**
 * PaymentOfferPicker — 选一个**当前可服务**的运营支付活动（`app_payment_offers.id`）。
 *
 * 候选判据逐条复刻 admin 写侧的 `findActiveServableOfferForApp`（`server/src/lib/active-offer.ts`），
 * 它本身又与 C 端 serving 的 `isOfferServable` 逐条同源：
 *
 *   `app_id 匹配 ∧ is_active ∧ deleted_at IS NULL ∧ now ∈ [starts_at, ends_at)`
 *
 * 半开区间（右端点 exclusive）与两侧一致。**不放宽也不收严**：
 *
 * - 放宽（例如列出未开始的活动）→ 退回「选得到、保存不了」——写侧 `assertActiveOfferForApp`
 *   用同一个 `now` 判，未开始的 offer 保存时吃 `OPS_BANNER_OFFER_NOT_FOUND`。
 * - 收严（例如顺手按 eligibility 筛）→ 藏掉本该能选的活动。eligibility 是 **per-user** 判定，
 *   banner 对全体用户下发同一份 slides，serving 侧根本不看它（C 端 `isOfferServable` 的 doc 明写
 *   「不含 eligibility」）。picker 比 serving 严会变成另一种「运营找不到活动」。
 *
 * **全局位（`appId === null`）不可用**：offer 是 app-scoped、无单一 app 可绑，写侧
 * `assertActiveOfferForApp` 对 `bannerAppId === null` **无条件硬拒**。故此处直接禁用并说明，
 * 而不是列一堆选了必然 422 的候选。
 */

import type { PaymentOfferResponse } from '@novel-admin/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { paymentOffersApi } from '~/hooks/use-payment-offers';
import { fmtDateTime } from '~/lib/format';

/** 与写侧 / C 端同源的可服务判据（半开区间）。纯函数，便于单测钉住。 */
export function isOfferServableNow(offer: PaymentOfferResponse, at: Date): boolean {
  if (!offer.is_active) return false;
  const startsAt = new Date(offer.starts_at).getTime();
  const endsAt = new Date(offer.ends_at).getTime();
  const nowMs = at.getTime();
  return nowMs >= startsAt && nowMs < endsAt;
}

export function PaymentOfferPicker({
  value,
  appId,
  strictWindow,
  evaluateAt,
  onChange,
  onSelectionBlocked,
  context = 'banner-slide',
}: {
  /** 已选中的 offer id */
  value: string;
  /** banner 所属 App；`null` = 全局位，本 picker 不可用（见文件头）*/
  appId: string | null;
  /**
   * 是否**强制**候选落在投放窗口内。
   *
   * - banner slide 传 `slide.is_active`：服务端 `validateTarget` 首行 `if (!resultingActive) return`
   *   —— 未上架时整个 target 校验都跳过，「为尚未开始的活动预配一个下架 slide」是被刻意支持的
   *   工作流（原注释「否则无法下架 locale 错的死链」说明这条分流是有意设计）。
   * - 推送任务恒传 `true`：A4 §3.3 的校验没有「草稿」这一档，建任务即校验。
   */
  strictWindow: boolean;
  /**
   * 判窗口用的时刻。**默认 `now`，但消费方的判据时刻不一定是 now** ——
   * 推送按 A4 §3.3 用**投递时刻** `scheduled_at ?? now`；定时推送若按 now 判，
   * 会把「发送时才生效」的活动挡在候选外（或反之放进一个届时已过期的）。
   */
  evaluateAt?: Date;
  onChange: (offerId: string) => void;
  /**
   * 当前选中值在**本次上架状态下**是否会被写侧拒。draft 选了未开始的 offer、随后把开关打开时，
   * 候选列表会把它筛掉但 `value` 还在 —— 不上报的话 Save 仍可点，提交必吃 422。
   */
  onSelectionBlocked?: (blocked: boolean) => void;
  /**
   * 界面文案的上下文。本组件被 banner slide 与推送任务共用，而**两边的补救动作完全不同** ——
   * banner 可以「把本帧改回下架」，推送没有这一档；推送用投递时刻判窗口，banner 用当前时刻。
   * 说错了运营会照着一个做不到的提示去操作。
   */
  context?: 'banner-slide' | 'push-task';
}) {
  const offersQ = useQuery({
    // **appId 必须在第二段**：offer 的 create/update/activate/deactivate 失效的是
    // `['payment-offers', appId]` 前缀，把 'banner-slide-picker' 放第二段会让前缀匹配不上 ——
    // 配合 30s staleTime，刚建完活动就打开弹窗会看不到它。
    queryKey: ['payment-offers', appId, 'banner-slide-picker'],
    // is_active 交给端点筛（少传一批回来）；时间窗端点不筛，下面按同一判据在前端补齐
    // limit 取端点硬上限 200（P1-api-contract §7）。端点按创建时间倒序、只筛 is_active，
    // 过期但仍 is_active 的行会占额度 —— 超过 200 条时较老的有效 offer 会被静默截掉，
    // 故下面对截断显式提示，不让它无声发生。
    queryFn: () => paymentOffersApi.list({ app_id: appId as string, is_active: true, limit: 200 }),
    enabled: appId !== null,
  });

  // 判据求值的时刻取一次即可：候选列表是一次性渲染，不需要随秒跳动重算。
  //
  // 上架的 slide 严格按写侧判据筛（保存会被同一判据拒）；未上架的 slide 服务端跳过校验，
  // 故额外列出**尚未开始**的活动供预配 —— 但**已结束的两种情况都不列**：那是死链，
  // 预配它没有任何将来能生效的路径。
  const atMs = evaluateAt?.getTime();
  const list = useMemo(() => {
    const at = atMs === undefined ? new Date() : new Date(atMs);
    return (offersQ.data?.data ?? []).filter((o) => {
      if (isOfferServableNow(o, at)) return true;
      if (strictWindow) return false;
      return o.is_active && new Date(o.starts_at).getTime() > at.getTime();
    });
  }, [offersQ.data, strictWindow, atMs]);
  // 已上架 + 选中值不在严格候选里 = 保存必被 assertActiveOfferForApp 拒。
  // 注意与 selectedMissing 区分：那条讲「值不在候选、可能已失效」，这条讲「这次保存一定失败」。
  // 首页没取全时要说出来：否则「没找到活动」与「活动被截断了」在界面上一模一样
  const truncated = offersQ.data?.hasMore === true;
  // **只在「一次完整成功的查询」之后才敢判 blocked**：query pending / 失败 / 结果被 limit 截断时，
  // 一个有效 offer 完全可能不在 `offersQ.data` 里 —— 据此锁 Save 会把本来合法的 slide 永久卡住，
  // 比原本要防的 422 更糟（422 至少能重试）。判不出就不拦，交服务端最终裁决。
  const lookupComplete = offersQ.isSuccess && !truncated;
  const selectionBlocked =
    lookupComplete &&
    strictWindow &&
    !!value &&
    !(offersQ.data?.data ?? []).some(
      (o) =>
        o.id === value && isOfferServableNow(o, atMs === undefined ? new Date() : new Date(atMs)),
    );
  useEffect(() => {
    onSelectionBlocked?.(selectionBlocked);
    // 卸载时解除阻断，避免切到别的 target 类型后 Save 被永久锁住
    return () => onSelectionBlocked?.(false);
  }, [selectionBlocked, onSelectionBlocked]);
  const upcomingIds = useMemo(() => {
    const nowMs = atMs === undefined ? Date.now() : atMs;
    return new Set(list.filter((o) => new Date(o.starts_at).getTime() > nowMs).map((o) => o.id));
  }, [list, atMs]);
  // 编辑既有 slide 时，它指向的 offer 可能已结束 / 已停用 —— 那它就不在候选集里。
  // **必须显式回显「有值但失效」**：手输框里至少摆着那个 UUID 看得见，picker 若显示空，
  // 运营会以为压根没配、随手一存就把 target 悄悄改了或清了（picker 化新引入的失败面）。
  // 同 selectionBlocked：**查询没完整成功就不下这个判断**。冷缓存下 list 一开始恒为空，
  // 不 gate 的话每个既有 offer slide 打开都会先闪一句「不在可投放列表中」；查询失败或被
  // 200 条截断时，一个有效 offer 也会被误标成失效。
  const selectedMissing = lookupComplete && !!value && !list.some((o) => o.id === value);

  if (appId === null) {
    return (
      <div
        className="card-flat"
        style={{ padding: '10px 12px', fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.6 }}
      >
        {context === 'push-task' ? (
          <>请先在上方选择目标 App —— 运营活动属于单个 App，选定后才能列出它的可投放活动。</>
        ) : (
          <>
            全局位不能投放运营活动 —— 活动属于单个 App，全局位对所有 App 下发、无法绑定。
            <br />
            请到具体 App 的轮播位上配置活动帧。
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      {selectionBlocked && (
        <div
          className="card-flat"
          style={{
            padding: '8px 10px',
            marginBottom: 6,
            fontSize: 11.5,
            color: 'var(--fg-neg)',
            lineHeight: 1.6,
          }}
        >
          {context === 'push-task' ? (
            <>
              所选活动在<strong>投递时刻</strong>不在有效期内 —— 提交会被拒。
              <br />
              请改选一个届时生效的活动，或调整发送时间。
            </>
          ) : (
            <>
              本帧已切到<strong>上架</strong>，但所选活动当前不在投放窗口内 —— 保存会被拒。
              <br />
              请改选一个进行中的活动，或把本帧改回下架再保存。
            </>
          )}
        </div>
      )}
      {selectedMissing && (
        <div
          className="card-flat"
          style={{
            padding: '8px 10px',
            marginBottom: 6,
            fontSize: 11.5,
            color: 'var(--fg-warn)',
            lineHeight: 1.6,
          }}
        >
          当前已配活动 <span className="mono">{value.slice(0, 8)}</span> 不在可投放列表中 ——
          可能已结束、已停用或不属于本 App。
          <br />
          它仍是这一帧的现值；下面另选一个才会替换掉它。
        </div>
      )}
      <div style={{ maxHeight: 190, overflow: 'auto' }} className="card-flat">
        {list.map((o) => (
          <div
            key={o.id}
            onClick={() => onChange(o.id)}
            className="hstack"
            style={{
              gap: 8,
              padding: '7px 10px',
              cursor: 'pointer',
              background: o.id === value ? 'var(--bg-subtle)' : 'transparent',
            }}
          >
            <span style={{ fontSize: 11.5, fontWeight: 500, minWidth: 84 }}>{o.scenario}</span>
            {/* 预配项必须打标：它现在**不可上架**，运营要知道「存得下但开不了」 */}
            {upcomingIds.has(o.id) && (
              <span className="chip chip-warn" style={{ fontSize: 9 }}>
                未开始
              </span>
            )}
            {/* eligibility 只做**展示**、不参与过滤（资格是 per-user 判定，serving 侧不看它）。
                但同一 App 下允许存在 scenario 与窗口都相同、只有 eligibility 不同的并发活动 ——
                不显示的话两行长得一模一样，点哪个都「看着对」，存进去的却是不同 id。 */}
            <span
              className={o.eligibility === 'all' ? 'chip' : 'chip chip-info'}
              style={{ fontSize: 9.5 }}
            >
              {o.eligibility === 'all' ? '全部用户' : '新用户'}
            </span>
            <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-muted)', flex: 1 }}>
              {fmtDateTime(o.starts_at)} → {fmtDateTime(o.ends_at)}
            </span>
            {/* 上面几项仍可能重合（同 scenario + 同 eligibility + 同窗口），id 是最后的可区分依据 */}
            <span className="mono" style={{ fontSize: 9.5, color: 'var(--fg-faint)' }}>
              {o.id.slice(0, 8)}
            </span>
          </div>
        ))}
        {list.length === 0 && (
          // 空态必须写清收窄条件：以前手输会收到一个 422，现在是「列表里根本没有」——
          // 不说明的话运营会以为活动没建成，而实际成因通常是尚未开始或已结束
          <div style={{ padding: 12, fontSize: 12, color: 'var(--fg-muted)', lineHeight: 1.6 }}>
            该 App 下没有可选的活动。
            <br />
            {strictWindow ? (
              <>
                {context === 'push-task' ? (
                  <>
                    仅列出在<strong>投递时刻</strong>（定时发送则为该时刻，否则为现在）有效的活动。
                  </>
                ) : (
                  <>
                    本帧已上架，仅列出<strong>当前时刻在有效期内</strong>的活动 ——
                    尚未开始的活动需先把本帧改为下架才能预配。
                  </>
                )}
              </>
            ) : (
              <>
                本帧未上架，已含<strong>尚未开始</strong>
                的活动供预配；已结束的活动不列（无法再生效）。
              </>
            )}
          </div>
        )}
      </div>
      {truncated && (
        <div style={{ fontSize: 11.5, color: 'var(--fg-warn)', marginTop: 6 }}>
          该 App 的启用中活动超过一页，列表可能未含全部可投放活动 —— 若找不到目标活动，请先在
          「运营活动」页停用已过期的活动。
        </div>
      )}
      {offersQ.isError && (
        <div style={{ fontSize: 11.5, color: 'var(--fg-neg)', marginTop: 6 }}>
          活动列表加载失败，请重试
        </div>
      )}
    </div>
  );
}
